import { createSelector } from 'reselect';
import { combineReducers } from 'redux';
import * as R from 'ramda';

import * as types from 'constants/CAFA';
import {
  ScanningAlgorithm,
  ScanningNoSearchResultsHandler,
  ScanningBlacklistedModalHandler,
} from 'actions/scanner';
import { PrintingMSSettings } from 'types/PrintingMS';
import {
  CustomerPOSConfiguration,
  DEFAULT_CUSTOMER_POS_CONFIGURATION,
} from 'containers/Forms/Settings/views/Customers/types';
import { TYPE_LOGOUT } from 'constants/Login';
import {
  DEFAULT_SHORTCUT_CONFIGURATION,
  getIsConfigurableShortcutAction,
  KeyboardShortcutsConf,
  ShortcutAction,
} from 'constants/shortcuts';
import { RootState } from 'reducers';

import { DefaultDict } from '../utils';

import { getSelectedPosID } from './PointsOfSale';
import { getUserLoggedIn } from './Login';
import { getSelectedWarehouseID } from './warehouses';
import loadingReducer from './util/loadingReducer';

interface MetricsCollectorValue {
  config?: {
    frequency?: number;
  };
}

export const integrations = (state = {}, { type, payload }) => {
  switch (type) {
    case types.FETCH_CAFA_CONFIGS.SUCCESS:
      return { ...state, ...payload };
    case types.SAVE_CAFA_CONFIGS.SUCCESS:
      return {
        ...state,
        [payload.id]: payload,
      };
    case types.DELETE_CAFA_CONFIG.SUCCESS:
      // eslint-disable-next-line no-case-declarations
      const stateCopy = { ...state };
      delete stateCopy[payload.id];
      return stateCopy;
    case TYPE_LOGOUT:
      return null;
    default:
      return state;
  }
};

const loadingCafa = loadingReducer.createReducer(
  [types.FETCH_CAFA_CONFIGS.START],
  [types.FETCH_CAFA_CONFIGS.SUCCESS, types.FETCH_CAFA_CONFIGS.ERROR],
  true,
  true,
);

export default combineReducers({ integrations, loadingCafa });

export const getIsLoadingCafa = createSelector(
  (state: any) => state.cafaConfigs.loadingCafa,
  loadingState =>
    loadingReducer.getIsLoading(loadingState) ||
    loadingReducer.getIsInitState(loadingState),
);

/**
 * Maps a CAFA level to a number signifying that level's priority
 * This can be used for comparisons or sorting
 */
const rankLevel = (lvl: types.CAFA_ENTRY['level']): number =>
  ({
    [types.CAFA_LEVELS.Company]: 1,
    [types.CAFA_LEVELS.Warehouse]: 2,
    [types.CAFA_LEVELS.Pos]: 3,
    [types.CAFA_LEVELS.User]: 4,
  }[lvl]);

/**
 * Given a list of cafa entries, filters out those which are shadowed by more specific rules
 */
const hideDuplicates = (integrations: types.CAFA_ENTRY[]) =>
  integrations.filter(
    int =>
      !integrations
        .filter(i2 => i2.type === int.type && i2.name === int.name)
        .some(int2 => rankLevel(int2.level) > rankLevel(int.level)),
  );

const getCafaSlice = state => state.cafaConfigs;

/** Returns all entries that are stored in CAFA */
export const getAllCafaEntries = createSelector(
  getCafaSlice,
  slice => Object.values(slice?.integrations || {}) as types.CAFA_ENTRY[],
);

const getStored = createSelector(
  () => localStorage.getItem('cache: CAFA'),
  str => {
    if (!str) {
      return [];
    }
    try {
      return JSON.parse(str);
    } catch (e) {
      return [];
    }
  },
);

/**
 * Returns all CAFA entries that match the current company/warehouse/pos/user
 * Includes shadowed entries, but does not include entries from other locations
 */
export const getApplicableCafaEntries = createSelector(
  getAllCafaEntries,
  getSelectedPosID,
  getSelectedWarehouseID,
  getUserLoggedIn,
  (ints, posID, whID, user) => {
    return ints.filter(
      int =>
        int.level === 'Company' ||
        String(int.level_id) ===
          String(
            {
              Warehouse: whID,
              Pos: posID,
              User: user?.userID,
            }[int.level],
          ),
    );
  },
);

/** Returns all entries that are stored in CAFA that are active (no shadowed entries) */
export const getUnobstructedCafaEntries = createSelector(
  getApplicableCafaEntries,
  getIsLoadingCafa,
  getStored,
  (ints, loading, stored) => {
    const unobstructedEntries = hideDuplicates(
      (loading ? stored : ints).flatMap(i => (i ? [i] : [])),
    );
    localStorage.setItem('cache: CAFA', JSON.stringify(unobstructedEntries));
    return unobstructedEntries as types.CAFA_ENTRY[];
  },
);

/**
 * Returns the currently active entry for a given integration + type
 * If level is specified, returns the entry from that level
 * Otherwise returns only the topmost (unshadowed) entry
 */
export const getCafaEntry = <
  T extends types.CAFA_ENTRY['type'],
  N extends types.CAFA_ENTRY['name'],
  V extends types.CAFA_ENTRY['value']
>(
  name: N,
  type?: T,
  level?:
    | undefined
    | { level: types.CAFA_ENTRY['level']; id: types.CAFA_ENTRY['level_id'] },
) => (state): undefined | types.CAFA_ENTRY<T, N, V> =>
  level === undefined
    ? getUnobstructedCafaEntries(state).find(
        (ent): ent is types.CAFA_ENTRY<T, N, V> =>
          (ent.type ? ent.type === type : !type) && ent.name === name,
      )
    : getAllCafaEntries(state).find(
        (ent): ent is types.CAFA_ENTRY<T, N, V> =>
          (ent.type ? ent.type === type : !type) &&
          ent.name === name &&
          ent.level === level.level &&
          (level.level === 'Company' ||
            String(ent.level_id) === String(level.id)),
      );

const getCafaAsDict = createSelector(getUnobstructedCafaEntries, entries => {
  const byIntByType = DefaultDict(() => DefaultDict(() => null));
  entries.forEach(int => {
    byIntByType[int.type as any][int.name] = int.value;
  });
  return byIntByType as { [type: string]: { [name: string]: any } };
});
/**
 * Returns the value of the currently active entry for a given integration + type
 * @deprecated Possible duplicate
 */
export const getCafaEntry2 = <ConfigType = unknown>(
  integration,
  type = '',
  defaultValue: ConfigType = {} as ConfigType,
) => (state): ConfigType =>
  getCafaAsDict(state)[type][integration] ?? defaultValue;

export const getGeneralPrintingSettings = createSelector(
  getCafaEntry<
    'printing',
    any,
    {
      forcePatchscriptRender: {
        enabled: boolean;
        pixelWidth: number;
        scaleMultiplier: number;
      };
      zReport: {
        useBoReceipt: boolean;
      };
    }
  >('printing', undefined),
  conf =>
    R.mergeDeepLeft(conf?.value as any)({
      forcePatchscriptRender: {
        enabled: false,
        pixelWidth: 400,
        scaleMultiplier: 1,
      },
      zReport: {
        useBoReceipt: true,
      },
    }),
);

export const getScanningAlgorithm = createSelector(
  getCafaEntry<
    typeof types.INTEGRATION_TYPES.scanner,
    'scanning_algorithm',
    {
      steps: ScanningAlgorithm;
      noResultsHandler: ScanningNoSearchResultsHandler;
      modalScanHandler: ScanningBlacklistedModalHandler;
      minScanLength: number;
      avgTimeByChar: number;
      timeBeforeScanTest: number;
    }
  >('scanning_algorithm', types.INTEGRATION_TYPES.scanner, {
    level: 'Company',
    id: '',
  }),
  (
    conf,
  ): {
    steps: ScanningAlgorithm;
    noResultsHandler: ScanningNoSearchResultsHandler;
    modalScanHandler: ScanningBlacklistedModalHandler;
    minScanLength: number;
    avgTimeByChar: number;
    timeBeforeScanTest: number;
  } =>
    conf?.value ?? {
      steps: [
        [{ request: 'products', by: 'code2' }],
        [{ request: 'products', by: 'code' }],
        [{ request: 'products', by: 'name' }],
      ],
      noResultsHandler: 'notification',
      modalScanHandler: 'notification',
      minScanLength: 2,
      avgTimeByChar: 100,
      timeBeforeScanTest: 100,
    },
);

/**
 * Returns Customer Settings in POS from CAFA
 *
 * POS is gradually moving towards CAFA.
 * This Configuration is storing Customer specific Settings for the POS
 */
export const getGeneralCustomerSettings = createSelector(
  getCafaEntry<
    typeof types.INTEGRATIONS.posConfigurations.customer,
    typeof types.INTEGRATION_TYPES.posConfigurations,
    CustomerPOSConfiguration
  >(
    types.INTEGRATION_TYPES.posConfigurations,
    types.INTEGRATIONS.posConfigurations.customer,
  ),
  conf =>
    R.mergeDeepLeft(conf?.value as CustomerPOSConfiguration)(
      DEFAULT_CUSTOMER_POS_CONFIGURATION,
    ),
);

// TODO: Move to separate file?
/**
 * Returns the current config (as stored in CAFA) for the cdDelphi customer display
 */
export const getDelphiCustomerDisplayConfig = createSelector(
  getCafaEntry('cdDelphi', 'customerDisplay'),
  (conf): any => conf?.value ?? {},
);

/**
 * Returns CAFA integration for selected integrations per type (level: "Company", ... value {payment: "x", printing: "y", etc...})
 */
export const getActiveIntegrationsConfig = getCafaEntry<
  any,
  any,
  { [integrationName: string]: any }
>('active-integrations', types.INTEGRATION_TYPES.integrations);

/**
 * Returns the object of CAFA integration for selected integrations per type ({payment: "x", printing: "y", etc...})
 */
export const getActiveIntegrationsValue = createSelector(
  getActiveIntegrationsConfig,
  (slice): { [integrationName: string]: string } => slice?.value ?? {},
);

export const getActivePaymentIntegration = createSelector(
  getActiveIntegrationsValue,
  slice => slice?.payment ?? 'external',
);

// In case we have more integrations supporting vault, a list with all vault supported integrations must be created
// and we need to check if the current integration is in the list. Instead of only looking for cayan
export const getIsCardBoardingEnabled = createSelector(
  getAllCafaEntries,
  getActiveIntegrationsValue,
  (entries, activeIntegration) => {
    const vaultEntry = entries.find(entry => entry.name === 'cayan');
    return !!(
      vaultEntry?.value?.cayanVault && activeIntegration.payment === 'cayan'
    );
  },
);

type CAFAPrintingMSSettings = types.CAFA_ENTRY<
  string,
  string,
  PrintingMSSettings
>;

/**
 * Returns topmost entry of Go Printing Microservice Settings.
 *
 * Any missing fields will be filled with values from lower level entries
 */
export const getPrintingMSSettings = (state): CAFAPrintingMSSettings | null => {
  const type = types.INTEGRATION_TYPES.printer;
  const name = types.INTEGRATIONS[type].goMS;
  const printingMSSettings = getApplicableCafaEntries(state)
    .filter(
      (ent): ent is CAFAPrintingMSSettings =>
        ent.name === name && ent.type === type,
    )
    .sort((a, b) => rankLevel(b.level) - rankLevel(a.level))
    .reduce(
      (merged, current) =>
        R.mergeDeepLeft(merged, {
          ...current,
          value: { enabled: 0, ...current.value },
        }),
      {} as CAFAPrintingMSSettings,
    );
  return Object.keys(printingMSSettings).length ? printingMSSettings : null;
};

/**
 * POS Brazil Version selected by the user for the whole company
 */
export const getPOSVersionsDictFromCAFA = createSelector(
  getCafaEntry<
    typeof types.INTEGRATION_TYPES.global,
    typeof types.INTEGRATIONS.global.posBrazilVersion,
    {
      develop: string;
      sandbox: string;
      production: string;
    }
  >(
    types.INTEGRATIONS.global.posBrazilVersion,
    types.INTEGRATION_TYPES.global,
    {
      level: types.CAFA_LEVELS.Company,
      id: '',
    },
  ),
  conf =>
    conf?.value ?? {
      develop: '',
      sandbox: '',
      production: '',
    },
);

export const getPatchScriptReceiptTemplates = createSelector(
  getApplicableCafaEntries,
  cafaConfigs =>
    cafaConfigs.filter(
      config => config.type === types.INTEGRATION_TYPES.patchScriptTemplate,
    ),
);

export const getMetricsCollectorFrequency = createSelector(
  getCafaEntry<undefined, 'metrics-collector', MetricsCollectorValue>(
    'metrics-collector',
  ),
  entry => entry?.value?.config,
);

export const DEFAULT_GRID_BUTTON_SIZE = 1;
export const DEFAULT_GRID_BUTTON_FONT_SIZE = 14;

type GridButtonSizeConfig = { buttonSize: number; fontSize: number };

export const getGridButtonSizeConfig = createSelector(
  state =>
    getCafaEntry<
      typeof types.INTEGRATION_TYPES.posConfigurations,
      typeof types.INTEGRATIONS.posConfigurations.gridButton,
      GridButtonSizeConfig
    >(
      types.INTEGRATIONS.posConfigurations.gridButton,
      types.INTEGRATION_TYPES.posConfigurations,
    )(state),
  config => ({
    buttonSize: DEFAULT_GRID_BUTTON_SIZE,
    fontSize: DEFAULT_GRID_BUTTON_FONT_SIZE,
    ...((config?.value ?? {}) as GridButtonSizeConfig),
  }),
);

export const getAllowedShortcutActions = createSelector(
  (state: any) =>
    getCafaEntry<
      typeof types.INTEGRATION_TYPES.posConfigurations,
      typeof types.INTEGRATIONS.posConfigurations.allowedKeyboardShortcutActions,
      KeyboardShortcutsConf
    >(
      types.INTEGRATIONS.posConfigurations.allowedKeyboardShortcutActions,
      types.INTEGRATION_TYPES.posConfigurations,
    )(state),
  config => ({
    ...DEFAULT_SHORTCUT_CONFIGURATION,
    ...(config?.value ?? {}),
  }),
);

export const getIsShortcutActionAllowedFunction = (state: RootState) => {
  const allowedKeyboardShortcutActionsConf = getAllowedShortcutActions(state);
  return (action?: ShortcutAction) => {
    if (!action) {
      return false;
    }
    return (
      !getIsConfigurableShortcutAction(action) ||
      // If configurable - check if configured to be enabled
      allowedKeyboardShortcutActionsConf[action]
    );
  };
};

export const getIsShortcutActionAllowed = (shortcutAction?: ShortcutAction) => (
  state: RootState,
) => {
  return getIsShortcutActionAllowedFunction(state)(shortcutAction);
};

export const getIsLoggerMsLoggingEnabled = createSelector(
  state =>
    getCafaEntry<
      typeof types.INTEGRATION_TYPES.posConfigurations,
      typeof types.INTEGRATIONS.posConfigurations.loggerMs,
      { enabled: boolean }
    >(
      types.INTEGRATIONS.posConfigurations.loggerMs,
      types.INTEGRATION_TYPES.posConfigurations,
    )(state),
  entry => entry?.value?.enabled,
);
